import { Box, Grid } from '@mui/material';
import { MprcFormSubHeader, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { MprcRequestIndicatorType } from 'components/MprcRequestIndicator';
import { MprcSimpleDialog } from 'components/MprcSimpleDialog';
import { ButtonCancel, ButtonConfirm } from 'components/buttons';
import { EnrollmentTrainingAgreementResponse } from 'generated/CoreApi_v1';
import { enqueueSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    enrollmentId: string;
    onClose: (agree: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////////

export const TrainingAgreementDialog: React.FC<IProps> = ({ enrollmentId, onClose }: IProps) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const [loading, setLoading] = useState<MprcRequestIndicatorType | null>(null);
    const [agreementData, setAgreementData] = useState<EnrollmentTrainingAgreementResponse | null>();
    const [error, setError] = useState<any>();

    const handleAgree = () => {
        setLoading("post");
        api.v1.lmsTraining.trainingSetAgreement({
            agreementId: agreementData!.agreementId,
            enrollmentId,
            deviceInfo: window.navigator.userAgent,
            timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
            .then(result => {
                if (result.data === true) onClose(true);
                else enqueueSnackbar(t("Student.TrainingAgreement.AgreeErrorMessage"), { variant: "error" });
            })
            .catch(e => setError(e))
            .finally(() => setLoading(null));
    };

    useEffect(() => {
        setLoading("request");
        api.v1.enrollment.getTrainingAgreement({ enrollmentId })
            .then(response => setAgreementData(response.data))
            .catch(e => setError(e))
            .finally(() => setLoading(null));
    }, []);


    if (agreementData == null) {
        if (loading) return <MprcRequestIndicator type={"request"} />;
        return <></>;
    }

    return (
        <MprcSimpleDialog
            title={t("Student.TrainingAgreement.Title")}
            message={
                error
                    ? <MprcRequestErrorReport error={error} />
                    : loading != null
                        ? <MprcRequestIndicator type={loading ?? "request"} />
                        : <>
                            <MprcFormSubHeader title={t("Student.TrainingAgreement.Subtitle")} />

                            <Grid width="100%" sx={{ border: "1px solid grey", marginTop: (theme) => theme.spacing(1), padding: (theme) => theme.spacing(2) }}>
                                <Box
                                    component="div"
                                    sx={{ height: "30vh", width: "100%", overflowY: "auto" }}
                                    dangerouslySetInnerHTML={{ __html: agreementData.text }}
                                />
                            </Grid>
                        </>
            }
            onClose={() => onClose(false)}
            actionsPosition="center"
            actions={
                [
                    <ButtonCancel
                        key="cancel"
                        text={t("Student.TrainingAgreement.NotAcceptButtonText")}
                        onClick={() => onClose(false)}
                        color="error"
                        variant="contained"
                        disabled={loading && error}
                    />,
                    <ButtonConfirm
                        key="confirm"
                        text={t("Student.TrainingAgreement.AcceptButtonText")}
                        onClick={() => handleAgree()}
                        variant="contained"
                        disabled={loading && error}
                    />
                ]
            }
        />
    );
};
